import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, faqItems } from '@/mocks/comics';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const featuredComics = comics.filter((c) => c.featured);
  const latestComics = comics.filter((c) => c.isNew);
  const popularComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 8);
  const recentUpdates = [...comics].sort(
    (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
  ).slice(0, 8);

  const displayCategories = categories.filter((c) => c.slug !== 'all');

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % featuredComics.length);
  }, [featuredComics.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + featuredComics.length) % featuredComics.length);
  }, [featuredComics.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '韩漫网拥有好看的韩国漫画大全，实现韩国漫画全集免费看，专属下拉式模式让韩漫在线阅读更轻松。',
    inLanguage: 'zh-Hans',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            {featuredComics.map((comic, index) => (
              <div
                key={comic.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover"
                  title={`${comic.title} - 韩国漫画全集免费在线阅读`}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
              </div>
            ))}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
              <div className="max-w-3xl w-full">
                <h2 className="text-3xl md:text-5xl font-bold font-heading text-background-50 mb-4">
                  韩国漫画全集免费看
                </h2>
                <p className="text-sm md:text-lg text-background-200/90 mb-8 max-w-xl mx-auto leading-relaxed">
                  韩漫网拥有好看的韩国漫画大全，专属下拉式模式让韩漫在线阅读更轻松，畅享沉浸式阅读体验。
                </p>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <Link
                    to="/category/all"
                    className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    立即浏览
                  </Link>
                  <Link
                    to="/about"
                    className="border border-background-200/50 text-background-50 hover:bg-background-50/10 px-6 py-3 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-background-50/20 hover:bg-background-50/40 rounded-full text-background-50 transition-colors z-20 cursor-pointer"
              aria-label="Previous slide"
            >
              <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-background-50/20 hover:bg-background-50/40 rounded-full text-background-50 transition-colors z-20 cursor-pointer"
              aria-label="Next slide"
            >
              <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
              {featuredComics.map((comic, index) => (
                <button
                  key={comic.id}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    index === currentSlide
                      ? 'bg-primary-500 w-6'
                      : 'bg-background-50/50 hover:bg-background-50/80'
                  }`}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                漫画分类
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                全部分类 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {displayCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors">
                    {cat.slug === 'romance' && <i className="ri-heart-line text-primary-600 text-lg"></i>}
                    {cat.slug === 'fantasy' && <i className="ri-magic-line text-primary-600 text-lg"></i>}
                    {cat.slug === 'action' && <i className="ri-sword-line text-primary-600 text-lg"></i>}
                    {cat.slug === 'drama' && <i className="ri-film-line text-primary-600 text-lg"></i>}
                    {cat.slug === 'comedy' && <i className="ri-emotion-happy-line text-primary-600 text-lg"></i>}
                    {cat.slug === 'thriller' && <i className="ri-search-eye-line text-primary-600 text-lg"></i>}
                    {cat.slug === 'daily' && <i className="ri-sun-line text-primary-600 text-lg"></i>}
                  </div>
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* Latest Updates */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                    最新上架
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">刚刚上架的新番韩漫，快来尝鲜</p>
                </div>
              </div>
              {latestComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {latestComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-foreground-400 py-10">暂无最新上架漫画</p>
              )}
            </div>
          </section>

          {/* Popular & Hot Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">全网最受欢迎的韩国漫画</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {popularComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </section>

          {/* Recent Updates Table */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                    最近更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">实时追踪最新连载进度</p>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs font-semibold text-foreground-500 bg-background-100 border-b border-background-200/70">
                  <span className="col-span-5">漫画名称</span>
                  <span className="col-span-2">作者</span>
                  <span className="col-span-2">最新章节</span>
                  <span className="col-span-2">更新时间</span>
                  <span className="col-span-1">状态</span>
                </div>
                {recentUpdates.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3 items-center border-b border-background-200/50 last:border-0 hover:bg-background-100/50 transition-colors cursor-pointer"
                  >
                    <div className="col-span-1 md:col-span-5 flex items-center gap-3">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-10 h-14 object-cover rounded flex-shrink-0"
                        loading="lazy"
                      />
                      <div>
                        <span className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors line-clamp-1">
                          {comic.title}
                        </span>
                        <span className="text-xs text-foreground-400 md:hidden">{comic.author}</span>
                      </div>
                    </div>
                    <span className="col-span-2 text-xs text-foreground-500 hidden md:block">{comic.author}</span>
                    <span className="col-span-2 text-xs text-foreground-600 hidden md:block">
                      第{comic.chapters}话
                    </span>
                    <span className="col-span-2 text-xs text-foreground-400">{comic.updateTime}</span>
                    <span className={`col-span-1 text-xs whitespace-nowrap ${comic.status === '连载中' ? 'text-accent-600' : 'text-secondary-600'}`}>
                      {comic.status}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">
                常见问题
              </h2>
              <div className="flex flex-col gap-3">
                {faqItems.map((item, index) => (
                  <details
                    key={index}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors list-none">
                      <h4 className="text-sm font-medium">{item.question}</h4>
                      <i className="ri-add-line group-open:ri-subtract-line text-foreground-400 transition-all"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative w-full py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20beautiful%20abstract%20illustration%20with%20warm%20coral%20and%20golden%20tones%2C%20soft%20gradient%20waves%2C%20dreamy%20atmosphere%2C%20artistic%20composition%2C%20elegant%20flowing%20lines%2C%20minimalist%20aesthetic%2C%20warm%20inviting%20colors%2C%20no%20text%2C%20abstract%20background&width=1600&height=500&seq=home-cta-bg&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
            <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-background-50 mb-4">
                开始你的韩漫之旅
              </h2>
              <p className="text-sm md:text-base text-background-200/90 mb-8">
                海量韩国漫画免费看，下拉式阅读更畅快
              </p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-8 py-3 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                立即开始阅读
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}